/**
 * Name review — split the existing persons' names into given names and surnames
 * (P16b G1, Pedro 2026-10-10).
 *
 * Pedro's decisions:
 *   - existing persons: "suggest a split to confirm" — the app proposes a split of the
 *     full name by the agreed rule (utils/nameSplit) and someone confirms or corrects
 *     each person here;
 *   - "anyone, RMs filed for approval" — open to every writer; an RM's confirmations go
 *     through approval like any RM change to a person; Admin / Compliance apply at once;
 *   - inline staged editing (C-339): a confirmed or corrected row turns orange, the
 *     header shows "N unsaved" with Discard / Save changes, one update per person,
 *     failures stay staged, and leaving offers Save & leave.
 * Lists natural persons still missing a given name or a surname; a saved row leaves
 * the list.
 */
import {
  Alert, Anchor, Badge, Button, Checkbox, Container, Group, Pagination, Stack, Table, Text,
  TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import { personsApi } from '../api/persons';
import type { Person } from '../api/types';
import { useAlertModal } from '../components/AlertModalProvider';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { useCurrentUser } from '../hooks/useAuth';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { usePermissions } from '../hooks/usePermissions';
import { personsKeys, usePersons } from '../hooks/usePersons';
import { suggestNameSplit } from '../utils/nameSplit';

const PAGE_SIZE = 50;

interface Staged { name: string; given: string; surnames: string }

/** What the row offers before anyone touches it: what is on record, else the suggestion. */
function proposal(p: Person): { given: string; surnames: string } {
  const s = suggestNameSplit(p.display_name);
  return { given: p.given_names ?? s.given, surnames: p.surnames ?? s.surnames };
}

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export function PersonNameReviewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showAlert } = useAlertModal();
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const canWrite = can('record.write');
  const autoApplies = user?.role === 'admin' || user?.role === 'compliance';
  const [page, setPage] = useState(1);
  const [staged, setStaged] = useState<Record<string, Staged>>({});
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error } = usePersons({
    missing_name_parts: true, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
  });
  const rows = data?.items ?? [];
  const stagedCount = Object.keys(staged).length;

  function stage(p: Person, patch: Partial<Staged>) {
    setStaged((prev) => {
      const base = prev[p.id] ?? { name: p.display_name, ...proposal(p) };
      return { ...prev, [p.id]: { ...base, ...patch } };
    });
  }
  function unstage(id: string) {
    setStaged((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }

  async function save(): Promise<boolean> {
    const entries = Object.entries(staged);
    if (entries.length === 0) return true;
    setSaving(true);
    let applied = 0;
    let filed = 0;
    const saved: string[] = [];
    const failed: string[] = [];
    for (const [id, s] of entries) {
      const given = s.given.trim();
      const surnames = s.surnames.trim();
      if (!given || !surnames) {
        failed.push(`${s.name}: both Given names and Surnames are needed`);
        continue;
      }
      try {
        const r = await personsApi.update(id, { given_names: given, surnames });
        if (r.pending) filed += 1; else applied += 1;
        saved.push(id);
      } catch (e) {
        failed.push(`${s.name}: ${e instanceof ApiError ? e.detail : String(e)}`);
      }
    }
    setStaged((prev) => {
      const n = { ...prev };
      for (const id of saved) delete n[id];
      return n;
    });
    await qc.invalidateQueries({ queryKey: personsKeys.all });
    await qc.invalidateQueries({ queryKey: ['change-requests'] });
    await qc.invalidateQueries({ queryKey: ['compliance'] });
    setSaving(false);
    if (saved.length > 0) {
      const parts = [
        applied ? `saved for ${people(applied)}` : '',
        filed ? `filed for approval for ${people(filed)}` : '',
      ].filter(Boolean);
      notifications.show({
        color: filed ? 'blue' : 'green', title: 'Name split', message: `Names ${parts.join(' · ')}.`,
      });
    }
    if (failed.length > 0) {
      showAlert({
        color: 'red',
        title: 'Some names could not be saved',
        message: `${failed.join(' · ')}. These rows are still unsaved (orange).`,
      });
    }
    return failed.length === 0;
  }

  // Leaving with staged rows → the guard offers Save & leave.
  useDirtyFormGuard(stagedCount > 0, save);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const readOnlyReason = canWrite ? null : 'Read-only users cannot change persons.';

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Group gap="xs">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/persons')}>Persons</Button>
            <Title order={2}>Name review</Title>
          </Group>
          <Group gap="sm">
            {stagedCount > 0 && (
              <>
                <Badge color="orange" variant="light">{stagedCount} unsaved</Badge>
                <Button variant="default" disabled={saving} onClick={() => setStaged({})}>Discard</Button>
                <Button loading={saving} onClick={() => void save()}
                  title={autoApplies ? undefined : 'Your changes will be filed for approval'}>
                  Save changes
                </Button>
              </>
            )}
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          Natural persons whose given names or surnames are not yet recorded (the client record asks
          for both). Each row proposes a split of the full name: the first two words are the given
          names when the name has four or more words, otherwise the first; da / de / do / dos / das / e
          go with the surname that follows. Correct a row, or tick Confirm to accept it, then
          Save changes. {autoApplies ? '' : 'Your changes are filed for approval.'}
        </Text>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error instanceof ApiError ? error.detail : 'The list could not be loaded.'}
          </Alert>
        )}
        {isLoading && <Text c="dimmed">Loading…</Text>}
        {!isLoading && rows.length === 0 && !error && (
          <Text c="dimmed">Every natural person has given names and surnames. Nothing to review.</Text>
        )}

        {rows.length > 0 && (
          <>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">{data?.total ?? 0} to review</Text>
              <Tooltip label={readOnlyReason ?? 'Accept the proposed split for every row on this page'}
                withArrow disabled={false}>
                <span>
                  <Button size="xs" variant="light" disabled={!canWrite}
                    onClick={() => rows.forEach((p) => { if (!staged[p.id]) stage(p, {}); })}>
                    Confirm all on this page
                  </Button>
                </span>
              </Tooltip>
            </Group>
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Full name</Table.Th>
                  <Table.Th>Given names</Table.Th>
                  <Table.Th>Surnames</Table.Th>
                  <Table.Th w={110}>Confirm</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((p) => {
                  const s = staged[p.id];
                  const shown = s ?? { name: p.display_name, ...proposal(p) };
                  const orange = s ? { styles: { input: { background: 'var(--mantine-color-orange-0)', borderColor: 'var(--mantine-color-orange-5)' } } } : {};
                  return (
                    <Table.Tr key={p.id}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Anchor component={Link} to={`/persons/${p.id}`} size="sm">{p.display_name}</Anchor>
                          <PendingCRBadge crs={p.pending_change_requests} />
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <TextInput size="xs" value={shown.given} disabled={!canWrite} aria-label={`Given names of ${p.display_name}`}
                          onChange={(e) => stage(p, { given: e.currentTarget.value })} {...orange} />
                      </Table.Td>
                      <Table.Td>
                        <TextInput size="xs" value={shown.surnames} disabled={!canWrite} aria-label={`Surnames of ${p.display_name}`}
                          onChange={(e) => stage(p, { surnames: e.currentTarget.value })} {...orange} />
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={readOnlyReason ?? (s ? 'Unsaved — Save changes or untick' : 'Accept this split')} withArrow>
                          <span>
                            <Checkbox checked={!!s} disabled={!canWrite} color="orange"
                              aria-label={`Confirm the split for ${p.display_name}`}
                              onChange={(e) => (e.currentTarget.checked ? stage(p, {}) : unstage(p.id))} />
                          </span>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center">
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
